// ---------------------------------------------------------------------------
// Momento da vista (F2 T4): "que horas a vista está mostrando" e a volta (tempo -> posição).
//
// momentoDaVista: horário de ABERTURA da vela mais nova visível, a partir do estado ancorado (posição relativa à
// série, AnchoredNav.relFor) e da série processada. Mesma regra de janela da fatia do App (dataToRender): a vista
// mostra `visible` velas terminando em `len - floor(distância)`, presa à janela mais antiga quando passa dela.
//
// vistaParaMomento: o inverso. Tempo T + série carregada -> distância do fim (e índice global) que põe a vela de T
// (a de abertura <= T; em lacuna, a anterior) como a mais nova visível; ou `fimDosDados` se T é anterior a tudo o
// que está carregado. Ida e volta exata para velas com pelo menos `visible - 1` velas mais antigas carregadas; antes
// disso a janela fica presa ao começo (`noInicio`).
//
// Puro (sem React, sem rede). Usado hoje só pelos testes; é a peça que o F3 liga à troca de série no mesmo momento.
//
// O que o F3 precisa (não feito aqui):
//   1. Carregar um bloco AO REDOR de T (não contíguo ao que já está carregado) quando `vistaParaMomento` der
//      `fimDosDados` ou `noInicio`: a série passa a ter dois trechos (o do presente e o de T) até se juntarem.
//   2. Mostrar "carregando" enquanto o bloco de T não chega (a vista não salta para outro momento no meio).
//   3. Cortar em "início dos dados — fonte" quando a fonte responder fimDosDados (CandleSource) antes de T.
//   4. Manter a ponta ao vivo SEPARADA do trecho de T (velas novas não podem deslocar a vista que está em T: é a
//      política 'mesmasVelas' daqui, mas sobre uma série com buracos).
// ---------------------------------------------------------------------------

import { NAV_VISIBLE_CANDLES } from './historyNav';
import { newestAbsOf, type AnchoredNav } from './anchoredNav';

export interface TimedPoint { timestamp: number }

/** Índice (na série, 0 = mais antiga) da vela mais nova visível com a vista a `dist` velas do fim. -1 se vazia. */
export function newestVisibleIndex(len: number, dist: number, visible = NAV_VISIBLE_CANDLES): number {
  if (len <= 0) return -1;
  const windowSize = Math.min(len, visible);
  const end = Math.max(windowSize, len - Math.floor(dist > 0 ? dist : 0));
  return end - 1;
}

/** Horário de abertura da vela mais nova visível (null = série vazia). `dist` = posição relativa a esta série. */
export function momentoDaVista(series: readonly TimedPoint[], dist: number, visible = NAV_VISIBLE_CANDLES): number | null {
  const i = newestVisibleIndex(series.length, dist, visible);
  return i < 0 ? null : series[i].timestamp;
}

/** O mesmo a partir do estado ancorado: posição relativa à série etiquetada (tagNewestAbs). */
export function momentoDaVistaNav(nav: AnchoredNav, series: readonly TimedPoint[], visible = NAV_VISIBLE_CANDLES): number | null {
  return momentoDaVista(series, nav.relFor(newestAbsOf(series)), visible);
}

export type VistaNoTempo =
  | { kind: 'ok'; dist: number; global: number; exact: boolean; noInicio: boolean }
  | { kind: 'fimDosDados' }
  | { kind: 'vazio' };

/**
 * Tempo T -> posição. `dist`: distância do fim relativa a esta série; `global`: índice global da vela (NaN se a
 * série não está etiquetada); `exact`: existe vela com abertura exatamente T; `noInicio`: a vela é uma das
 * `visible - 1` mais antigas carregadas (a janela não consegue pô-la na borda direita).
 * T depois da vela mais nova = presente (dist 0).
 */
export function vistaParaMomento(T: number, series: readonly TimedPoint[], visible = NAV_VISIBLE_CANDLES): VistaNoTempo {
  const len = series.length;
  if (len === 0) return { kind: 'vazio' };
  if (T < series[0].timestamp) return { kind: 'fimDosDados' };
  let lo = 0, hi = len - 1;                                   // maior i com timestamp <= T
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (series[mid].timestamp <= T) lo = mid; else hi = mid - 1; }
  const dist = len - 1 - lo;
  return { kind: 'ok', dist, global: newestAbsOf(series) + dist, exact: series[lo].timestamp === T, noInicio: lo < Math.min(len, visible) - 1 };
}

// ---------------------------------------------------------------------------
// F3b C1: momento do CENTRO da vista e o inverso. Na troca de timeframe ou de ativo o ponto fixo é a vela do centro
// da tela: o horário dela vira o centro da série nova.
//
// Centro = a vela `floor(janela/2)` velas mais antiga que a mais nova visível (janela = min(len, visible)): com 1.000
// velas visíveis, a 501ª a partir da direita. Mesma regra de janela de `newestVisibleIndex` (a do fatiador).
//
// O inverso escolhe a vela que CONTÉM T (a de abertura <= T; numa lacuna, a anterior; num timeframe maior, a vela
// maior em que T cai) e devolve a distância do fim que a põe no centro, com encosto:
//   'fim'    : a vela está entre as `janela/2` mais novas carregadas (o centro não chega lá): a vista fica no fim
//              dos dados (= presente, se a série contém o presente);
//   'inicio' : a vela está entre as `janela/2` mais antigas carregadas: a vista fica na borda mais antiga;
//   'depois' : T depois da vela mais nova (inclusive a duração dela, se `intervalMs` foi dado): fim dos dados.
// T antes da vela mais antiga carregada: `antesDaSerie` (quem chama decide: carregar mais antigo ou encostar no
// início real da fonte).
// ---------------------------------------------------------------------------

/** Velas entre o centro e a mais nova visível numa janela de `w` velas. */
export function centerOffset(w: number): number { return Math.floor(w / 2); }

/** Índice (0 = mais antiga) da vela do centro da vista a `dist` velas do fim. -1 se vazia. */
export function centerVisibleIndex(len: number, dist: number, visible = NAV_VISIBLE_CANDLES): number {
  const e = newestVisibleIndex(len, dist, visible);
  if (e < 0) return -1;
  return Math.max(0, e - centerOffset(Math.min(len, visible)));
}

/** Horário de abertura da vela do centro da vista (null = série vazia). `dist` = posição relativa a esta série. */
export function momentoDoCentro(series: readonly TimedPoint[], dist: number, visible = NAV_VISIBLE_CANDLES): number | null {
  const i = centerVisibleIndex(series.length, dist, visible);
  return i < 0 ? null : series[i].timestamp;
}

/** O mesmo a partir do estado ancorado (posição relativa à série etiquetada). */
export function momentoDoCentroNav(nav: AnchoredNav, series: readonly TimedPoint[], visible = NAV_VISIBLE_CANDLES): number | null {
  return momentoDoCentro(series, nav.relFor(newestAbsOf(series)), visible);
}

export type Encosto = 'fim' | 'inicio' | 'depois' | null;
export type CentroNoTempo =
  | { kind: 'ok'; dist: number; global: number; index: number; contains: boolean; encosto: Encosto }
  | { kind: 'antesDaSerie' }
  | { kind: 'vazio' };

/**
 * Tempo T -> distância do fim que põe a vela que contém T no centro da vista. `index` = índice da vela na série;
 * `contains` = T cai dentro dela (abertura <= T < abertura + intervalMs; sem intervalMs: abertura === T).
 */
export function centroParaMomento(T: number, series: readonly TimedPoint[], intervalMs?: number, visible = NAV_VISIBLE_CANDLES): CentroNoTempo {
  const len = series.length;
  if (len === 0) return { kind: 'vazio' };
  if (T < series[0].timestamp) return { kind: 'antesDaSerie' };
  let lo = 0, hi = len - 1;                                   // maior i com timestamp <= T
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (series[mid].timestamp <= T) lo = mid; else hi = mid - 1; }
  const w = Math.min(len, visible), half = centerOffset(w), maxDist = len - w;
  const contains = intervalMs ? T < series[lo].timestamp + intervalMs : series[lo].timestamp === T;
  let dist = (len - 1 - lo) - half, encosto: Encosto = null;
  if (lo === len - 1 && intervalMs !== undefined && !contains) { dist = 0; encosto = 'depois'; }
  else if (dist < 0) { dist = 0; encosto = 'fim'; }
  else if (dist > maxDist) { dist = maxDist; encosto = 'inicio'; }
  return { kind: 'ok', dist, global: newestAbsOf(series) + dist, index: lo, contains, encosto };
}
